import { LoadingButton } from '@mui/lab'
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material'
import { getOrganizationalUsers } from 'api/organization'
import { getOrganizationalSubUsers } from 'api/users'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { useCohortsFilter } from 'features/Cohorts/hooks/useCohortsFilter'
import { useOrgStateUnitFilter } from 'features/UI'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useShowControl } from 'hooks/useShowControl'
import { SyntheticEvent, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { BaseTableRequest, FilterModel, UserTypeTab } from 'types'
import { getRoute, useGetDataSource } from 'utils'
import { useOrgRoleFilter } from '../../hooks/useOrgRoleFilter'
import { useUserOrgColumns } from '../../hooks/useUserOrgColumns'
import { useUserOrgTraineeColumns } from '../../hooks/useUserOrgTraineeColumns'
import { useTraineeStatusFilter } from '../../hooks/useTraineeStatusFilter'
import { useUserStatusFilter } from '../../hooks/useUserStatusFilter'
import { InviteTraineeModal } from '../InviteTraineeModal/InviteTraineeModal'
import { orgTraineeCSVFields } from './csvFields'

export function UsersOrganizationPage() {
  const navigate = useNavigate()
  const { userType } = useParams()

  const selectedTab = useMemo(() => {
    if (!userType) return UserTypeTab.TRAINEE

    return userType as UserTypeTab
  }, [userType])

  const handleChangeSelectedTab = useCallback(
    (_: SyntheticEvent, newValue: UserTypeTab) => {
      const route = getRoute(routes.orgUsers, { userType: newValue })
      navigate(route)
    },
    [navigate],
  )

  const [isInviteOpen, onOpenInvite, onCloseInvite] = useShowControl()

  const { RoleFilter, rolesFilterValue } = useOrgRoleFilter(selectedTab, 'type')
  const { UserStatusFilter, userStatusFilterValue } = useUserStatusFilter()
  const { TraineeStatusFilter, traineeStatusFilterValue } = useTraineeStatusFilter()
  const { OrgStateUnitFilter, orgStateUnitFilterValue } = useOrgStateUnitFilter()
  const { CohortsFilter: UserTagsFilter, cohortsFilterValue: userTagsFilterValue } =
    useCohortsFilter()

  const apiMethod = useMemo(() => {
    if (selectedTab === UserTypeTab.TRAINEE) {
      return getOrganizationalSubUsers
    }

    return getOrganizationalUsers
  }, [selectedTab])

  const getOrgUsersWrapper = useCallback(
    (params: BaseTableRequest) => {
      let extendFilter: FilterModel = params.filter || {}

      if (rolesFilterValue) {
        extendFilter = {
          ...extendFilter,
          ...rolesFilterValue,
        }
      }

      const statusFilterValue =
        selectedTab === UserTypeTab.TRAINEE ? traineeStatusFilterValue : userStatusFilterValue
      if (statusFilterValue) {
        extendFilter.userStatus = statusFilterValue
      }

      if (orgStateUnitFilterValue) {
        if (selectedTab === UserTypeTab.TRAINEE) {
          extendFilter.accessCodeId = orgStateUnitFilterValue
        }
        if (selectedTab === UserTypeTab.ORG) {
          extendFilter.ownedAccessCodes = orgStateUnitFilterValue
        }
      }
      if (selectedTab === UserTypeTab.ORG && userTagsFilterValue) {
        extendFilter.cohortId = userTagsFilterValue
      }

      return apiMethod({
        ...params,
        filter: extendFilter,
      })
    },
    [
      apiMethod,
      rolesFilterValue,
      userStatusFilterValue,
      traineeStatusFilterValue,
      orgStateUnitFilterValue,
      selectedTab,
      userTagsFilterValue,
    ],
  )

  const getDataSource = useGetDataSource(getOrgUsersWrapper)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSource)

  const { csvLoading, onExportCSV } = useExportCSV(
    getOrgUsersWrapper,
    gridApi,
    columnApi,
    orgTraineeCSVFields,
    'Trainees.csv',
  )

  const traineeColumnDefs = useUserOrgTraineeColumns()
  const orgUsersColumnDefs = useUserOrgColumns()

  const columns = useMemo(() => {
    if (selectedTab === UserTypeTab.TRAINEE) {
      return traineeColumnDefs
    }

    return orgUsersColumnDefs
  }, [orgUsersColumnDefs, selectedTab, traineeColumnDefs])

  const left = (
    <>
      <Tabs value={selectedTab} onChange={handleChangeSelectedTab} indicatorColor="primary">
        <Tab value={UserTypeTab.TRAINEE} label={getTitle('Trainees')} />
        <Tab value={UserTypeTab.ORG} label={getTitle('Organizational Users')} />
      </Tabs>
    </>
  )

  return (
    <>
      <TableToolbar
        left={left}
        exportCSVEnable={selectedTab === UserTypeTab.TRAINEE}
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
      >
        {selectedTab === UserTypeTab.TRAINEE && (
          <LoadingButton onClick={onOpenInvite} variant="contained" sx={{ minWidth: 120 }}>
            Invite
          </LoadingButton>
        )}
      </TableToolbar>

      <Typography variant="subtitle1" mb={1}>
        {selectedTab === UserTypeTab.TRAINEE && 'Your Registered Trainees and Their Training Time'}
        {selectedTab === UserTypeTab.ORG && 'Leader Registration, Current Status and Roles'}
      </Typography>

      {selectedTab === UserTypeTab.TRAINEE && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="280px">{TraineeStatusFilter}</Box>
          {OrgStateUnitFilter}
        </Stack>
      )}

      {selectedTab === UserTypeTab.ORG && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="280px">{RoleFilter}</Box>
          <Box minWidth="280px">{UserStatusFilter}</Box>
          <Box minWidth="220px">{UserTagsFilter}</Box>
          {OrgStateUnitFilter}
        </Stack>
      )}

      <InfinityAgGrid pagination columnDefs={columns} onGridReady={onGridReady} />

      <InviteTraineeModal
        isOpen={isInviteOpen}
        handleClose={onCloseInvite}
        onInvited={() => gridApi?.purgeInfiniteCache()}
      />
    </>
  )
}
